
import { useState } from "react";
import HabitGridCell from "./HabitGridCell";
import { useIsMobile } from "@/hooks/use-mobile";
import { Habit } from "@/context/SupabaseHabitContext";
import HabitInfoPopup from "./HabitInfoPopup";

interface HabitGridBodyProps {
  gridData: (Date | null)[][];
  months: {name: string, index: number}[];
  weekdays: string[];
  editMode: boolean;
  getIsCompleted: (date: Date | null) => boolean;
  onToggleDay: (date: Date | null) => void;
  cellColors: {
    empty: string;
    filled: string;
  };
  habit?: Habit; // Optional habit for the popup
  allHabits?: Habit[]; // Optional all habits for the popup
}

export default function HabitGridBody({
  gridData,
  months,
  weekdays,
  editMode,
  getIsCompleted,
  onToggleDay,
  cellColors,
  habit,
  allHabits = [],
}: HabitGridBodyProps) {
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  
  // Handle cell info click
  const handleCellInfoClick = (date: Date, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setPopupPosition({ 
      x: Math.min(rect.right + 10, window.innerWidth - 330), // Prevent popup from going off-screen
      y: Math.min(rect.top, window.innerHeight - 250)
    });
    setSelectedDate(date);
  };
  
  // Adjust dimensions based on mobile or desktop
  const cellRowHeight = isMobile ? "15px" : "20px";
  const weekdayColumnWidth = isMobile ? "w-4" : "w-6";
  const monthLabelLeftCalc = (index: number) => `${index * (isMobile ? 15 : 20)}px`;
  
  return (
    <div className="overflow-x-auto w-full">
      <div className="min-w-max">
        {/* Month labels - Fixed positioning and width */}
        <div className="flex h-6 relative mb-1">
          <div className={`${weekdayColumnWidth} mr-2`}></div> {/* Spacer for alignment with weekday column */}
          <div className="flex-1 relative">
            {months.map((month, i) => (
              <div 
                key={i} 
                className="absolute text-xs text-muted-foreground"
                style={{ 
                  left: monthLabelLeftCalc(month.index),
                }}
              >
                {month.name}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex">
          {/* Weekday labels - Fixed width, exactly aligned with rows */}
          <div className={`flex flex-col ${weekdayColumnWidth} mr-2`}>
            {weekdays.map((day, index) => (
              <div
                key={index}
                className="flex items-center justify-center text-xs text-muted-foreground leading-none mb-1"
                style={{ height: cellRowHeight }}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Grid cells */}
          <div className="flex flex-col">
            {gridData.map((row, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className={`flex items-center ${isMobile ? 'gap-[2px]' : 'gap-1'} mb-1`}
                style={{ height: cellRowHeight }}
              >
                {row.map((date, colIndex) => (
                  <HabitGridCell
                    key={`cell-${rowIndex}-${colIndex}`}
                    date={date}
                    isCompleted={getIsCompleted(date)}
                    canToggle={editMode}
                    onClick={() => onToggleDay(date)}
                    onInfoClick={date && !editMode ? (e) => handleCellInfoClick(date, e) : undefined}
                    color={cellColors}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Popup for habit details */}
      {selectedDate && habit && (
        <HabitInfoPopup
          date={selectedDate}
          habits={allHabits.length > 0 ? allHabits : [habit]}
          completedHabits={getIsCompleted(selectedDate) ? [habit] : []}
          position={popupPosition}
          onClose={() => setSelectedDate(null)}
        />
      )}
      
      {/* Backdrop for closing popup when clicking outside */}
      {selectedDate && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
}
